import { getNativeBridgeFunction } from 'libs/shared-ui/src/commonUtils/nativeBridge';
import { ConnectError } from '@bufbuild/connect';
import {
  DocumentViewer,
  LinkButton,
  deserify,
  getMediaBasePath,
  useCommonServiceClientContext,
  useFeatureEnabled,
  ReportButton,
  getDeviceType,
} from '@geneo2-web/shared-ui';
import { Box, SxProps } from '@mui/material';
import { FileEnum } from '@protos/content_management/content.db_pb';
import { Feature } from '@protos/school_management/school.db_pb';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useGlobalContext } from '../../../app/Context/GlobalContextProvider';
import { useAppSelector } from '../../../reduxStore/reduxHooks';
import { setToastInfo } from '../../Home/reducer/homeDashboard.slice';
import { useLocation } from 'react-router-dom';
import { useDownloadContext } from '../../../app/Context/DownloadContextProviderV2';
import { interactionEvent } from '../../Auth/auth.events';
import { AttemptTypeEnum } from '@protos/learning_management/lms.db_pb';

interface IViewReport {
  assessmentId: bigint;
  reportButtonHandler?: () => void;
  attemptNumber?: number;
  isBestAttempt?: boolean;
  rootStyles?: SxProps;
  subject?: string;
  className?: string;
  section?: string;
  isSummaryReportDisabled?: boolean;
}

export default function ViewBulkReportButton(props: IViewReport) {
  const location = useLocation();
  const pathSegments = location.pathname.split('/');
  const { isOffline } = useDownloadContext();
  const { SchoolAssessmentAPIServiceV2ClientWithStatusCodeHandler } =
    useCommonServiceClientContext();
  const user = deserify(useAppSelector((state) => state.auth.user_info));
  const featureInfo = user?.schoolDetails[0]?.featuresPlanInfo;
  const isViewReportsEnabled = useFeatureEnabled(
    featureInfo,
    Feature.VIEW_REPORTS
  );
  const isBulkReportsEnabled = useFeatureEnabled(
    featureInfo,
    Feature.BULK_REPORTS
  );
  const isSummaryReportsEnabled =
    useFeatureEnabled(featureInfo, Feature.SUMMARIZED_REPORTS) &&
    !props.isSummaryReportDisabled;
  const { setShowAppLoader } = useGlobalContext();
  const [pdfUrl, setPdfUrl] = useState<string | undefined>(undefined);
  const [isSummaryPdf, setIsSummaryPdf] = useState(false);
  const dispatch = useDispatch();

  // ------------------------------------------------------------

  const fetchSchoolAssessmentReport = async (isSummaryReport?: boolean) => {
    try {
      setShowAppLoader(true);
      const res =
        await SchoolAssessmentAPIServiceV2ClientWithStatusCodeHandler.fetchStudentsAssessmentBulkReport(
          {
            assessmentId: props.assessmentId,
            attemptNumber: props.isBestAttempt
              ? undefined
              : props.attemptNumber,
            attemptType: props.isBestAttempt
              ? AttemptTypeEnum.ATTEMPT_TYPE_BEST
              : undefined,
            isSummaryReport: isSummaryReport,
          }
        );
      if (res.data?.bulkReportUrl) {
        setIsSummaryPdf(!!isSummaryReport);
        if (getNativeBridgeFunction('redirectToBrowser')) {
          const url = getMediaBasePath(res.data.bulkReportUrl, undefined, true);
          try {
            getNativeBridgeFunction('redirectToBrowser')?.(url);
          } catch (error) {
            window.open(url, '_blank');
          }
        } else {
          const url = getMediaBasePath(res.data.bulkReportUrl);
          window.open(url, '_blank');
        }
      } else {
        dispatch(
          setToastInfo({
            label: res.message,
            variant: 'success',
            open: true,
          })
        );
      }
      setShowAppLoader(false);
    } catch (err) {
      if (err instanceof ConnectError) {
        console.log('Error:', err.rawMessage);
        dispatch(
          setToastInfo({
            label: err.rawMessage,
            variant: 'error',
            open: true,
          })
        );
      } else {
        dispatch(
          setToastInfo({
            label: 'Error while generating reports',
            variant: 'error',
            open: true,
          })
        );
      }
      setShowAppLoader(false);
    }
  };

  // ------------------------------------------------------------

  if (!isViewReportsEnabled || !isBulkReportsEnabled) {
    return null;
  }

  const buttonList = [
    {
      name: 'Detailed Report',
      onClick: async () => {
        props.reportButtonHandler
          ? props.reportButtonHandler()
          : fetchSchoolAssessmentReport();
        await interactionEvent({
          context: 'Your_Assessment',
          name: 'BULK_STUDENT_DETAILED_REPORT',
          pathSegments: pathSegments,
          isOffline: isOffline,
        });
      },
    },
  ];

  if (isSummaryReportsEnabled) {
    buttonList.push({
      name: 'Summary Report',
      onClick: async () => {
        fetchSchoolAssessmentReport(true);
        await interactionEvent({
          context: 'Your_Assessment',
          name: 'BULK_STUDENT_SUMMARY_REPORT',
          pathSegments: pathSegments,
          isOffline: isOffline,
        });
      },
    });
  }

  return (
    <Box sx={props.rootStyles}>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <ReportButton buttonText="Student Reports" buttonList={buttonList} />
      </Box>

      {pdfUrl && (
        <DocumentViewer
          downloadUrl={pdfUrl}
          previewUrl={pdfUrl}
          gcpUrl={pdfUrl}
          type={FileEnum.FILE_TYPE_PDF}
          fileName={
            props.subject || props.className || props.section
              ? `${props.subject || ''}_${props.className || ''}_${
                  props.section || ''
                }_Geneo_Assessment_${
                  isSummaryPdf ? 'Summary_Report' : 'Report'
                }_Teacher_${props.assessmentId}.pdf`
              : `Geneo_Assessment_${
                  isSummaryPdf ? 'Summary_Report' : 'Report'
                }_Teacher_${props.assessmentId}.pdf`
          }
          open={!!pdfUrl}
          closeFile={() => {
            setPdfUrl(undefined);
            setIsSummaryPdf(false);
          }}
        />
      )}
    </Box>
  );
}
